// Cosmetic dev-console deterrents.
//
// IMPORTANT: this is COSMETIC hardening only — none of these techniques
// meaningfully prevent a determined viewer from opening DevTools,
// inspecting the DOM, or downloading network responses. Anything
// rendered client-side is by definition accessible client-side. What
// this module does buy us:
//
//   1. Casual right-clickers can't easily "Save video as…" / "Copy
//      video address" via the browser's default context menu.
//   2. F12 / Ctrl+Shift+I / Cmd+Opt+I / Ctrl+U keyboard shortcuts get
//      swallowed on our origin, so opening the console requires a
//      menu-bar interaction (browser menu ▸ Tools ▸ Developer Tools)
//      that most non-technical viewers won't reach for.
//   3. An open-DevTools heuristic prints a warning to the console
//      itself, so someone who opens it gets a friendly "yeah, we
//      see you" message.
//
// For real content protection use signed short-lived URLs on the
// backend (recommended pattern documented in the "b" option that
// was NOT taken this iteration).
//
// Returns a teardown function that removes all installed listeners
// so the guard can be un-installed cleanly (useful in HMR / tests /
// owner-mode where you might want DevTools access preserved).

const DEV_KEY_COMBOS = [
    // { key, ctrl?, shift?, meta?, alt? }
    { key: "F12" },
    { key: "I", ctrl: true, shift: true },
    { key: "J", ctrl: true, shift: true },
    { key: "C", ctrl: true, shift: true },
    { key: "U", ctrl: true }, // view source
    { key: "I", meta: true, alt: true }, // macOS DevTools
    { key: "J", meta: true, alt: true }, // macOS console
    { key: "C", meta: true, alt: true }, // macOS inspector
    { key: "U", meta: true, alt: true }, // macOS view source
];

function matchesCombo(e, combo) {
    if (e.key.toUpperCase() !== combo.key.toUpperCase()) return false;
    if (combo.ctrl && !e.ctrlKey) return false;
    if (combo.shift && !e.shiftKey) return false;
    if (combo.meta && !e.metaKey) return false;
    if (combo.alt && !e.altKey) return false;
    return true;
}

export function installConsoleGuard({ enabled = true } = {}) {
    if (typeof window === "undefined" || !enabled) return () => {};

    // 1. Right-click / long-press context menu suppression.
    // Applied at capture phase on the document so overlays and
    // portals inherit the block without each having to opt in.
    // We ALLOW context menu inside form controls (input, textarea,
    // contenteditable) so viewers can still paste into the search
    // banner and owners into the owner-bar text fields.
    const onContextMenu = (e) => {
        const t = e.target;
        if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
            return;
        }
        e.preventDefault();
    };

    // 2. Keyboard shortcut suppression for DevTools + View Source.
    // Also captured — we don't want a child overlay to have already
    // eaten the event before we get a look. As with the context menu
    // we let form-control editing shortcuts through so paste /
    // select-all still work.
    const onKeyDown = (e) => {
        const t = e.target;
        const inEditable = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
        for (const combo of DEV_KEY_COMBOS) {
            if (matchesCombo(e, combo)) {
                // Ctrl+U (view source) has no place inside inputs so
                // block it unconditionally; the DevTools shortcuts are
                // also safe to swallow in inputs because Chrome maps
                // them to browser-level actions anyway (they wouldn't
                // interact with input text).
                void inEditable;
                e.preventDefault();
                e.stopPropagation();
                return;
            }
        }
    };

    // 3. Open-DevTools heuristic. Compares outerHeight - innerHeight
    // (and outerWidth - innerWidth) against a threshold that only
    // exceeds when a docked DevTools pane is open. Highly imperfect:
    // - Undocked DevTools (own window) is undetectable.
    // - Some viewers with browser extension pop-outs / mobile emulator
    //   viewports will fire a false positive.
    // - Firefox's Responsive Design mode also fires it.
    // So we only use it to print a friendly message once, never to
    // block or redirect. The threshold 160 is the conventional value
    // used by open-source DevTools-detector snippets.
    let warned = false;
    const HEURISTIC_THRESHOLD_PX = 160;
    const checkDevTools = () => {
        if (warned) return;
        const wDiff = Math.abs(window.outerWidth - window.innerWidth);
        const hDiff = Math.abs(window.outerHeight - window.innerHeight);
        if (wDiff > HEURISTIC_THRESHOLD_PX || hDiff > HEURISTIC_THRESHOLD_PX) {
            warned = true;
            try {
                // Use plain console.log — the styled ASCII block prints
                // as a big "hey" message when the viewer opens DevTools.
                // No hostile language; this is a shared retro-TV app,
                // not a bank.
                console.log(
                    "%c▣ SCARTLEAD.TV ▣\n" +
                    "%cYou've opened the developer console.\n" +
                    "This is a retro CRT TV toy — the video URLs you'll\n" +
                    "find here are hosted externally (MEGA) or streamed\n" +
                    "through my backend. Please be kind and don't\n" +
                    "redistribute the tapes. If you want to add a\n" +
                    "channel or share the site, tune in via the guide!",
                    "font-family: 'VT323', monospace; font-size: 24px; color: #33FF33; text-shadow: 0 0 8px #33FF33;",
                    "font-family: 'VT323', monospace; font-size: 14px; color: #33FF33;",
                );
            } catch { /* console blocked in some sandboxes */ }
        }
    };
    // Debounce via setInterval — polling every 1s is imperceptible
    // and cheap enough. Cleared in the teardown.
    const heuristicHandle = window.setInterval(checkDevTools, 1000);

    window.document.addEventListener("contextmenu", onContextMenu, { capture: true });
    window.document.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
        window.document.removeEventListener("contextmenu", onContextMenu, { capture: true });
        window.document.removeEventListener("keydown", onKeyDown, { capture: true });
        window.clearInterval(heuristicHandle);
    };
}
